'use client';

import { useId } from 'react';
import Link from "@/components/ui/Link";

interface RecentNewsProps {
  title: string;
  moreText?: string;
  href?: string;
  background?: string;
  backgroundImage?: {
    url: string;
    enabled: boolean;
  };
  /** Text color override when using custom background */
  textColor?: string;
  /** Desktop font size for the title (explicit override) */
  fontSize?: string;
  /** Mobile font size for the title (explicit override). Falls back to the
   *  level-based mobile size when not provided. */
  fontSizeMobile?: string;
  /** Heading level (1-6) - determines font size if fontSize not provided */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
}

// Desktop font sizes based on heading level (h1-h6)
const HEADING_LEVEL_SIZES: Record<number, string> = {
  1: '32px',   // h1
  2: '24px',   // h2
  3: '22px',   // h3
  4: '20px',   // h4
  5: '17px',   // h5
  6: '15px',   // h6
};

// Mobile font sizes - smaller than desktop
const MOBILE_HEADING_LEVEL_SIZES: Record<number, string> = {
  1: '24px',   // h1
  2: '18px',   // h2
  3: '16px',   // h3
  4: '15px',   // h4
  5: '14px',   // h5
  6: '12px',   // h6
};

export default function RecentNews({
  title = 'சமீபத்திய செய்திகள்',
  moreText = 'மேலும்',
  href = '#',
  background,
  backgroundImage,
  textColor,
  fontSize,
  fontSizeMobile,
  level,
}: RecentNewsProps) {
  // Build background styles
  const containerStyle: React.CSSProperties = {};

  if (background) {
    containerStyle.backgroundColor = background;
  }

  if (backgroundImage?.enabled && backgroundImage?.url) {
    containerStyle.backgroundImage = `url(${backgroundImage.url})`;
    containerStyle.backgroundSize = 'cover';
    containerStyle.backgroundPosition = 'center';
    containerStyle.backgroundRepeat = 'no-repeat';
  }

  // Determine font size: explicit fontSize > level-based > default
  const desktopFontSize = fontSize || (level ? HEADING_LEVEL_SIZES[level] : undefined);
  const mobileFontSize =
    fontSizeMobile || (level ? MOBILE_HEADING_LEVEL_SIZES[level] : undefined);

  const uniqueId = useId();
  const stableId = `rn-${uniqueId.replace(/:/g, '')}`;

  // Use custom text color or default blue
  const colorClass = textColor
    ? `text-${textColor.replace('#', '')}`
    : 'text-215EF9';
  const textStyle: React.CSSProperties = {
    ...(textColor ? { color: textColor } : {}),
    ...(desktopFontSize ? { fontSize: desktopFontSize } : {}),
  };

  return (
    <div
      className={`w-full ${!background && !backgroundImage?.enabled ? 'bg-F8F8F8' : ''}`}
      style={containerStyle}
    >
      {mobileFontSize && (
        <style>{`@media(max-width:767px){.${stableId}{font-size:${mobileFontSize} !important}}`}</style>
      )}
      <div className="w-full">
        <Link
          href={href}
          className="flex items-center justify-between px-4 py-3  transition-colors duration-200 cursor-pointer"
        >
          <div className="flex items-center gap-2">
            {/* <ArrowDown size={18} className={colorClass} style={textColor ? { color: textColor } : {}} /> */}
            <p className={`${stableId} font-bold leading-[1.4] line-clamp-1 ${colorClass}`} style={textStyle}>
              {title}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <p className={`text-12-tamil-600 line-clamp-1  ${colorClass}`}>{moreText}</p>
            {/* <ChevronRight size={16} className="text-215EF9" /> */}
          </div>
        </Link>
      </div>
    </div>
  );
}
